import { useState, useEffect } from 'react';

export function useLocalStorage<T>(
    key: string,
    initialValue: T,
    options?: {
        serialize?: (value: T) => string;
        deserialize?: (value: string) => T;
    }
) {
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    // Default serialization methods
    const serialize = options?.serialize ?? JSON.stringify;
    const deserialize = options?.deserialize ?? JSON.parse;

    // Get initial value from localStorage or use initialValue
    const [storedValue, setStoredValue] = useState<T>(() => {
        try {
            setIsLoading(true);
            const item = window.localStorage.getItem(key);
            const value = item ? deserialize(item) : initialValue;
            setIsLoading(false);
            return value;
        } catch (error) {
            setError(error as Error);
            setIsLoading(false);
            console.error('Error reading from localStorage:', error);
            return initialValue;
        }
    });

    // Update localStorage whenever storedValue changes
    useEffect(() => {
        try {
            window.localStorage.setItem(key, serialize(storedValue));
            setError(null);
        } catch (error) {
            setError(error as Error);
            console.error('Error writing to localStorage:', error);
        }
    }, [key, storedValue, serialize]);

    const remove = () => {
        try {
            window.localStorage.removeItem(key);
            setStoredValue(initialValue);
            setError(null);
        } catch (error) {
            setError(error as Error);
            console.error('Error removing from localStorage:', error);
        }
    };

    return {
        value: storedValue,
        setValue: setStoredValue,
        remove,
        isLoading,
        error
    };
} 